import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Storage as StorageIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useOwner } from '../../contexts/OwnerContext';
import api from '../../utils/axios';

interface Client {
  id: string;
  name: string;
  status: string;
  package: string;
  storageUsed: number;
  storageLimit: number;
  userLimit: number;
  createdAt: string;
  updatedAt: string;
  _count: {
    users: number;
    claims: number;
    clients: number;
  };
  users?: User[];
  billing?: BillingInfo;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  created_at: string;
  phone?: string;
}

interface BillingInfo {
  plan: string;
  status: string;
  nextBilling: string;
  amount: number;
}

const ClientManagement: React.FC = () => {
  const { 
    tenants, 
    loading, 
    errors, 
    loadTenants, 
    updateTenant,
    createTenant
  } = useOwner();
  
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  
  const [editData, setEditData] = useState({
    status: '',
    package: '',
    storageLimit: 0,
    userLimit: 0
  });

  const [createData, setCreateData] = useState({
    name: '',
    email: '',
    password: '',
    package: 'BASIC',
    storageLimit: 10,
    userLimit: 5,
    firstName: '',
    lastName: '',
    phone: '',
    address: ''
  });

  const [passwordResetData, setPasswordResetData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const [clientDetails, setClientDetails] = useState<Client | null>(null);
  const [clientUsers, setClientUsers] = useState<User[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const handleAddClient = () => {
    setSelectedClient(null);
    setIsCreating(true);
    setCreateData({
      name: '',
      email: '',
      password: '',
      package: 'BASIC',
      storageLimit: 10,
      userLimit: 5,
      firstName: '',
      lastName: '',
      phone: '',
      address: ''
    });
    setOpenDialog(true);
  };

  const handleEditClient = (client: Client) => {
    setSelectedClient(client);
    setIsCreating(false);
    setEditData({
      status: client.status,
      package: client.package,
      storageLimit: client.storageLimit,
      userLimit: client.userLimit
    });
    setOpenDialog(true);
  };

  const handleViewClientDetails = async (client: Client) => {
    setClientDetails(client);
    setLoadingDetails(true);
    try {
      // Fetch detailed client information
      const [usersResponse, billingResponse] = await Promise.all([
        api.get(`/owner/clients/${client.id}/users`),
        api.get(`/owner/clients/${client.id}/billing`)
      ]);
      
      setClientUsers(usersResponse.data.users || []);
      setClientDetails({
        ...client,
        users: usersResponse.data.users,
        billing: billingResponse.data.billing
      });
    } catch (error) {
      console.error('Error fetching client details:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load client details',
        severity: 'error'
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handlePasswordReset = async (userId: string) => {
    if (passwordResetData.newPassword !== passwordResetData.confirmPassword) {
      setSnackbar({
        open: true,
        message: 'Passwords do not match',
        severity: 'error'
      });
      return;
    }

    try {
      await api.post(`/owner/users/${userId}/reset-password`, {
        newPassword: passwordResetData.newPassword
      });
      
      setSnackbar({
        open: true,
        message: 'Password reset successfully',
        severity: 'success'
      });
      
      setPasswordResetData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error resetting password:', error);
      setSnackbar({
        open: true,
        message: 'Failed to reset password',
        severity: 'error'
      });
    }
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        // Create new client
        const result = await createTenant(createData);
        setSnackbar({
          open: true,
          message: `Client "${result.tenant.name}" created successfully! Admin user: ${result.adminUser.email}`,
          severity: 'success'
        });
      } else {
        // Update existing client
        await updateTenant(selectedClient!.id, editData);
        setSnackbar({
          open: true,
          message: `Client "${selectedClient!.name}" updated successfully!`,
          severity: 'success'
        });
      }
      setOpenDialog(false);
      setSelectedClient(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving client:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to save client',
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'error';
      case 'cancelled':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPackageColor = (packageType: string) => {
    switch (packageType) {
      case 'BASIC':
        return 'default';
      case 'PROFESSIONAL':
        return 'primary';
      case 'ENTERPRISE':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (loading.tenants) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (errors.tenants) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading tenants: {errors.tenants}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Client Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClient}
        >
          Add Client
        </Button>
      </Box>

      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Client Name</TableCell>
                <TableCell>Package</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Storage</TableCell>
                <TableCell>Claims</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {tenant.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tenant.package}
                      color={getPackageColor(tenant.package) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tenant.status}
                      color={getStatusColor(tenant.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Badge badgeContent={tenant._count.users} color="primary">
                      <PersonIcon />
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <StorageIcon fontSize="small" />
                      <Typography variant="body2">
                        {tenant.storageUsed.toFixed(1)} / {tenant.storageLimit} GB
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Badge badgeContent={tenant._count.claims} color="secondary">
                      <BusinessIcon />
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(tenant.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewClientDetails(tenant)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Client">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClient(tenant)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Client Details Dialog */}
      <Dialog 
        open={!!clientDetails} 
        onClose={() => setClientDetails(null)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <BusinessIcon />
            <Typography variant="h6">
              {clientDetails?.name} - Client Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : clientDetails ? (
            <Box sx={{ mt: 2 }}>
              <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                <Tab label="Overview" />
                <Tab label="Users" />
                <Tab label="Billing" />
                <Tab label="Settings" />
              </Tabs>

              {activeTab === 0 && (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Basic Information
                          </Typography>
                          <List dense>
                            <ListItem>
                              <ListItemText 
                                primary="Client Name" 
                                secondary={clientDetails.name} 
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemText 
                                primary="Status" 
                                secondary={
                                  <Chip 
                                    label={clientDetails.status} 
                                    color={getStatusColor(clientDetails.status) as any}
                                    size="small"
                                  />
                                } 
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemText 
                                primary="Package" 
                                secondary={
                                  <Chip 
                                    label={clientDetails.package} 
                                    color={getPackageColor(clientDetails.package) as any}
                                    size="small"
                                  />
                                } 
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemText 
                                primary="Created" 
                                secondary={new Date(clientDetails.createdAt).toLocaleDateString()} 
                              />
                            </ListItem>
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Usage Statistics
                          </Typography>
                          <List dense>
                            <ListItem>
                              <ListItemText 
                                primary="Users" 
                                secondary={`${clientDetails._count.users} / ${clientDetails.userLimit}`} 
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemText 
                                primary="Storage Used" 
                                secondary={`${clientDetails.storageUsed.toFixed(1)} / ${clientDetails.storageLimit} GB`} 
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemText 
                                primary="Total Claims" 
                                secondary={clientDetails._count.claims} 
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemText 
                                primary="Total Clients" 
                                secondary={clientDetails._count.clients} 
                              />
                            </ListItem>
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeTab === 1 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    User Management
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Last Login</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {clientUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              {user.firstName} {user.lastName}
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <Chip label={user.role} size="small" />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={user.status} 
                                color={getStatusColor(user.status) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Reset Password">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedClient(clientDetails);
                                    setActiveTab(3);
                                  }}
                                >
                                  <LockIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {activeTab === 2 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Billing Information
                  </Typography>
                  {clientDetails.billing ? (
                    <Card>
                      <CardContent>
                        <List dense>
                          <ListItem>
                            <ListItemText 
                              primary="Current Plan" 
                              secondary={clientDetails.billing.plan} 
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText 
                              primary="Billing Status" 
                              secondary={
                                <Chip 
                                  label={clientDetails.billing.status} 
                                  color={getStatusColor(clientDetails.billing.status) as any}
                                  size="small"
                                />
                              } 
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText 
                              primary="Next Billing" 
                              secondary={new Date(clientDetails.billing.nextBilling).toLocaleDateString()} 
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText 
                              primary="Monthly Amount" 
                              secondary={`$${clientDetails.billing.amount}`} 
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  ) : (
                    <Typography color="text.secondary">
                      No billing information available
                    </Typography>
                  )}
                </Box>
              )}

              {activeTab === 3 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Password Reset
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="New Password"
                        type="password"
                        value={passwordResetData.newPassword}
                        onChange={(e) => setPasswordResetData({ ...passwordResetData, newPassword: e.target.value })}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Confirm Password"
                        type="password"
                        value={passwordResetData.confirmPassword}
                        onChange={(e) => setPasswordResetData({ ...passwordResetData, confirmPassword: e.target.value })}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        startIcon={<SecurityIcon />}
                        onClick={() => {
                          // Handle password reset for selected user
                          if (clientUsers.length > 0) {
                            handlePasswordReset(clientUsers[0].id);
                          }
                        }}
                        disabled={!passwordResetData.newPassword || !passwordResetData.confirmPassword}
                      >
                        Reset Password
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientDetails(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Client Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {isCreating ? 'Add New Client' : `Edit ${selectedClient?.name}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {isCreating ? (
              // Create new client form
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Client Name"
                    margin="normal"
                    value={createData.name}
                    onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    margin="normal"
                    type="email"
                    value={createData.email}
                    onChange={(e) => setCreateData({ ...createData, email: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    margin="normal"
                    type="password"
                    value={createData.password}
                    onChange={(e) => setCreateData({ ...createData, password: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Package</InputLabel>
                    <Select
                      value={createData.package}
                      onChange={(e) => setCreateData({ ...createData, package: e.target.value })}
                      label="Package"
                    >
                      <MenuItem value="BASIC">Basic ($99/month)</MenuItem>
                      <MenuItem value="PROFESSIONAL">Professional ($299/month)</MenuItem>
                      <MenuItem value="ENTERPRISE">Enterprise ($599/month)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    margin="normal"
                    value={createData.firstName}
                    onChange={(e) => setCreateData({ ...createData, firstName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    margin="normal"
                    value={createData.lastName}
                    onChange={(e) => setCreateData({ ...createData, lastName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    margin="normal"
                    value={createData.phone}
                    onChange={(e) => setCreateData({ ...createData, phone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Storage Limit (GB)"
                    margin="normal"
                    type="number"
                    value={createData.storageLimit}
                    onChange={(e) => setCreateData({ ...createData, storageLimit: Number(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="User Limit"
                    margin="normal"
                    type="number"
                    value={createData.userLimit}
                    onChange={(e) => setCreateData({ ...createData, userLimit: Number(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    margin="normal"
                    multiline
                    rows={2}
                    value={createData.address}
                    onChange={(e) => setCreateData({ ...createData, address: e.target.value })}
                  />
                </Grid>
              </Grid>
            ) : (
              // Edit existing client form
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Package</InputLabel>
                    <Select
                      value={editData.package}
                      onChange={(e) => setEditData({ ...editData, package: e.target.value })}
                      label="Package"
                    >
                      <MenuItem value="BASIC">Basic</MenuItem>
                      <MenuItem value="PROFESSIONAL">Professional</MenuItem>
                      <MenuItem value="ENTERPRISE">Enterprise</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={editData.status}
                      onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="ACTIVE">Active</MenuItem>
                      <MenuItem value="SUSPENDED">Suspended</MenuItem>
                      <MenuItem value="CANCELLED">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Storage Limit (GB)"
                    margin="normal"
                    type="number"
                    value={editData.storageLimit}
                    onChange={(e) => setEditData({ ...editData, storageLimit: Number(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="User Limit"
                    margin="normal"
                    type="number"
                    value={editData.userLimit}
                    onChange={(e) => setEditData({ ...editData, userLimit: Number(e.target.value) })}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={isCreating && (!createData.name || !createData.email || !createData.password)}
          >
            {isCreating ? 'Create Client' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientManagement; 